/**
 * services/InstrumentService.ts
 * User instrument management service
 * 
 * Extends BaseService to provide CRUD operations for user's personal instrument library.
 * Handles importing from reference catalog, bulk operations, and custom instrument management.
 */

import { BaseService } from './BaseService';
import type { Instrument } from '@/types/schema/Instrument';
import type { AssetClassType } from '@/types/shared/AssetClass';
import type { EntityStatus } from '@/types/shared/EntityStatus';
import type { IncrementType } from '@/types/shared/IncrementType';
import { INCREMENT_TYPE_META } from '@/constants/incrementType';
import { getInstrumentsByAssetClass, getInstrumentById } from '@/data/instrumentCatalog';
import { shouldShowRawValue } from '@/utils/incrementType';
import { syncCoordinatorService } from './sync/SyncCoordinatorService';

/**
 * Result of import operation including status of each instrument
 */
export interface InstrumentImportResult {
  /** Successfully created instruments */
  created: Instrument[];
  
  /** Instruments that already exist (duplicates) */
  duplicates: Array<{
    catalogId: string;
    symbol: string;
    displayName: string;
    existingInstrument: Instrument;
  }>;
  
  /** Instruments that failed to import */
  errors: Array<{
    catalogId: string;
    error: string;
  }>;
}

/**
 * Result of bulk delete/archive operations
 */
export interface BulkDeleteResult {
  /** IDs of instruments successfully deleted/archived */
  successIds: string[];
  /** IDs of instruments that failed to delete/archive */
  failedIds: string[];
  /** Error messages keyed by instrument ID (only for failed operations) */
  errors?: Record<string, string>;
}

/**
 * Service for managing user's personal instrument library
 * 
 * Provides CRUD operations plus specialized methods for importing from
 * reference catalog and managing instrument relationships.
 */
export class InstrumentService extends BaseService<Instrument> {
  constructor() {
    super('instruments'); // Links to db.instruments table
  }

  // ===== IMPORT OPERATIONS =====

  /**
   * Import instruments from reference catalog to user library
   * 
   * @param catalogIds - Array of catalog instrument IDs to import
   * @param customizations - Optional customizations to apply during import
   * @param allowDuplicates - Whether to allow creating multiple instruments from same template (default: false)
   * @returns Promise resolving to import result with created instruments, duplicates, and errors
   */
  async importFromCatalog(
    catalogIds: string[], 
    customizations?: Array<Partial<Instrument>>,
    allowDuplicates: boolean = false
  ): Promise<InstrumentImportResult> {
    const importData: Array<Partial<Instrument>> = [];
    const result: InstrumentImportResult = {
      created: [],
      duplicates: [],
      errors: []
    };

    for (let i = 0; i < catalogIds.length; i++) {
      const catalogId = catalogIds[i];
      const customization = customizations?.[i] || {};
      
      // Skip if catalogId is undefined
      if (!catalogId) {
        result.errors.push({
          catalogId: catalogId || `index-${i}`,
          error: 'Catalog ID is undefined'
        });
        continue;
      }
      
      // Get reference instrument
      const catalogInstrument = getInstrumentById(catalogId);
      if (!catalogInstrument) {
        result.errors.push({
          catalogId,
          error: 'Catalog instrument not found'
        });
        continue;
      }

      // Check if user already has this instrument
      let displayName = catalogInstrument.name;
      try {
        const existing = await this.getByReferenceId(catalogId);
        if (existing.length > 0) {
          // Track as duplicate instead of skipping
          const existingInstrument = existing[0];
          if (existingInstrument) {
            result.duplicates.push({
              catalogId,
              symbol: catalogInstrument.symbol,
              displayName: catalogInstrument.name,
              existingInstrument
            });
          }
          
          // Skip creation unless duplicates are explicitly allowed
          if (!allowDuplicates) {
            continue;
          }
          
          // Generate unique display name if creating duplicates
          if (allowDuplicates) {
            // Find next available number (ES-1, ES-2, ES-3, etc.)
            const existingNumbers = existing
              .map((inst: Instrument) => {
                const match = inst.displayName.match(/-(\d+)$/);
                return match && match[1] ? parseInt(match[1], 10) : 1;
              })
              .sort((a: number, b: number) => a - b);
            
            let nextNumber = 1;
            for (const num of existingNumbers) {
              if (nextNumber === num) {
                nextNumber++;
              } else {
                break;
              }
            }
            
            displayName = `${catalogInstrument.name}-${nextNumber}`;
          }
        }
      } catch (error) {
        result.errors.push({
          catalogId,
          error: `Error checking existing instrument: ${error instanceof Error ? error.message : 'Unknown error'}`
        });
        continue;
      }

      // Convert catalog entry to user instrument format
      const isDisplayNameCustomized = displayName !== catalogInstrument.name;
      const instrumentData: Partial<Instrument> = {
        symbol: catalogInstrument.symbol,
        displayName,
        classification: catalogInstrument.classification,
        status: 'active',
        instrumentReferenceId: catalogId, // This will be a string, not undefined
        templateStatus: isDisplayNameCustomized ? 'custom' : 'up-to-date',
        customizedFields: isDisplayNameCustomized ? ['displayName'] : [], // Mark displayName as customized if duplicate name was generated
        
        // Copy trading specifications from catalog (user can customize)
        contractSize: catalogInstrument.contractSize,
        contractAsset: catalogInstrument.contractAsset,
        incrementSize: catalogInstrument.incrementSize,
        incrementType: catalogInstrument.incrementType,
        quoteCurrency: catalogInstrument.quoteCurrency,
        pricePrecision: this.calculatePrecisionFromIncrementSize(catalogInstrument.incrementSize),
        
        // Apply any user customizations
        ...customization
      };

      importData.push(instrumentData);
    }

    // Create instruments if any valid ones found
    if (importData.length > 0) {
      try {
        result.created = await this.bulkAdd(importData);
      } catch (error) {
        // If bulk add fails, treat all as errors
        result.errors.push({
          catalogId: 'bulk-operation',
          error: `Failed to create instruments: ${error instanceof Error ? error.message : 'Unknown error'}`
        });
      }
    }

    return result;
  }

  /**
   * Bulk import entire asset class from reference catalog
   * 
   * @param assetClass - Asset class to import (e.g., 'futures', 'forex')
   * @returns Promise resolving to import result with created instruments, duplicates, and errors
   */
  async bulkImportAssetClass(assetClass: AssetClassType): Promise<InstrumentImportResult> {
    const catalogInstruments = getInstrumentsByAssetClass(assetClass);
    const catalogIds = catalogInstruments.map(instrument => instrument.id);
    
    return await this.importFromCatalog(catalogIds);
  }

  // ===== QUERY OPERATIONS =====

  /**
   * Get user instruments linked to specific reference catalog instrument
   * 
   * @param referenceId - Reference catalog instrument ID
   * @returns Promise resolving to array of user instruments (usually 0 or 1)
   */
  async getByReferenceId(referenceId: string): Promise<Instrument[]> {
    try {
      const allInstruments = await this.getAll();
      return allInstruments.filter(instrument => 
        instrument.instrumentReferenceId === referenceId
      );
    } catch (error) {
      console.error(`Failed to get instruments by reference ID ${referenceId}:`, error);
      throw new Error('Failed to retrieve instruments by reference');
    }
  }

  /**
   * Search user instruments by symbol or name
   * 
   * @param query - Search query (symbol or display name)
   * @returns Promise resolving to matching instruments
   */
  async searchUserInstruments(query: string): Promise<Instrument[]> {
    if (!query.trim()) {
      return await this.getAll();
    }

    const searchTerm = query.toLowerCase().trim();
    
    try {
      const allInstruments = await this.getAll();
      return allInstruments.filter(instrument =>
        instrument.symbol.toLowerCase().includes(searchTerm) ||
        instrument.displayName.toLowerCase().includes(searchTerm)
      );
    } catch (error) {
      console.error(`Failed to search user instruments with query "${query}":`, error);
      throw new Error('Failed to search instruments');
    }
  }

  /**
   * Get user instruments by asset class
   *
   * Filters out tombstoned records via getAll() to ensure consistency with
   * other read methods. Uses in-memory filtering (acceptable for UI contexts).
   *
   * @param assetClass - Asset class to filter by
   * @returns Promise resolving to instruments in that asset class
   */
  async getByAssetClass(assetClass: AssetClassType): Promise<Instrument[]> {
    try {
      const allInstruments = await this.getAll();
      return allInstruments.filter(
        instrument => instrument.classification.assetClass === assetClass
      );
    } catch (error) {
      console.error(`Failed to get instruments by asset class ${assetClass}:`, error);
      throw new Error('Failed to retrieve instruments by asset class');
    }
  }

  /**
   * Get user instruments that need review (imported but not customized)
   * 
   * @returns Promise resolving to instruments needing review
   */
  async getInstrumentsNeedingReview(): Promise<Instrument[]> {
    return [];
  }

  // ===== UTILITY METHODS =====

  /**
   * Get statistics about user's instrument library
   * 
   * @returns Promise resolving to statistics object
   */
  async getLibraryStatistics(): Promise<{
    total: number;
    byAssetClass: Record<AssetClassType, number>;
    imported: number;
    custom: number;
  }> {
    try {
      const allInstruments = await this.getAll();
      
      const byAssetClass: Record<string, number> = {};
      let imported = 0;
      let custom = 0;

      allInstruments.forEach(instrument => {
        // Count by asset class
        byAssetClass[instrument.classification.assetClass] = (byAssetClass[instrument.classification.assetClass] || 0) + 1;
        
        // Count imported vs custom
        if (instrument.instrumentReferenceId) {
          imported++;
        } else {
          custom++;
        }
      });

      return {
        total: allInstruments.length,
        byAssetClass: byAssetClass as Record<AssetClassType, number>,
        imported,
        custom
      };
    } catch (error) {
      console.error('Failed to get library statistics:', error);
      throw new Error('Failed to generate library statistics');
    }
  }

  // ===== UPDATE OPERATIONS WITH AUTO-RECALCULATION =====

  /**
   * Update instrument with auto-recalculation of pricePrecision when incrementSize changes
   */
  override async update(id: string, updates: Partial<Instrument>): Promise<Instrument> {
    // If incrementSize is being changed, recalculate pricePrecision
    if ('incrementSize' in updates && updates.incrementSize !== undefined) {
      updates.pricePrecision = this.calculatePrecisionFromIncrementSize(updates.incrementSize);
    }
    
    return super.update(id, updates);
  }

  // ===== PRIVATE HELPER METHODS =====
  
  // ===== NEW CALCULATION HELPERS =====
  
  /**
   * Calculate tick/pip value from new catalog structure
   * @param instrument - Instrument with contractSize and incrementSize
   * @returns Dollar value per minimum price movement
   */
  public calculateTickValue(instrument: { contractSize: number; incrementSize: number }): number {
    return instrument.contractSize * instrument.incrementSize;
  }
  
  /**
   * Format contract specification for display
   * @param instrument - Instrument with new structure
   * @returns Formatted string like "100 troy oz" or "50 index points"
   */
  public formatContractSize(instrument: { contractSize: number; contractAsset: string }): string {
    return `${instrument.contractSize.toLocaleString()} ${instrument.contractAsset}`;
  }
  
  /**
   * Format minimum price movement for display
   * @param instrument - Instrument with new structure
   * @returns Formatted string like "1 tick = $0.25" or "$0.0001" for raw increments
   */
  public formatIncrementSize(instrument: { incrementSize: number; incrementType: IncrementType; quoteCurrency: string }): string {
    if (shouldShowRawValue(instrument.incrementType, INCREMENT_TYPE_META)) {
      const symbol = instrument.quoteCurrency === 'USD' ? '$' : instrument.quoteCurrency + ' ';
      return `${symbol}${instrument.incrementSize}`;
    }
    return `1 ${instrument.incrementType} = ${instrument.quoteCurrency === 'USD' ? '$' : instrument.quoteCurrency + ' '}${instrument.incrementSize}`;
  }
  
  /**
   * Format tick/pip value for display
   * @param instrument - Instrument with new structure
   * @returns Formatted string like "$12.50/tick" or "$0.10" for raw increments
   */
  public formatTickValue(instrument: { contractSize: number; incrementSize: number; incrementType: IncrementType; quoteCurrency: string; pricePrecision: number }): string {
    const value = this.calculateTickValue(instrument);
    const symbol = instrument.quoteCurrency === 'USD' ? '$' : instrument.quoteCurrency + ' ';

    if (shouldShowRawValue(instrument.incrementType, INCREMENT_TYPE_META)) {
      return `${symbol}${value.toFixed(instrument.pricePrecision)}`;
    }

    return `${symbol}${value.toFixed(instrument.pricePrecision)}/${instrument.incrementType}`;
  }

  /**
   * Calculate precision (decimal places) from incrementSize
   * @param incrementSize - The minimum price movement value
   * @returns Number of decimal places needed to represent this increment
   */
  public calculatePrecisionFromIncrementSize(incrementSize: number): number {
    if (Math.floor(incrementSize) === incrementSize) return 0;
    
    const str = incrementSize.toString();
    if (str.indexOf('.') !== -1 && str.indexOf('e-') === -1) {
      const decimalPart = str.split('.')[1];
      return decimalPart ? decimalPart.length : 0;
    } else if (str.indexOf('e-') !== -1) {
      const parts = str.split('e-');
      if (parts.length === 2 && parts[1]) {
        const exponent = parseInt(parts[1], 10);
        const mantissaParts = parts[0]?.split('.') || [];
        const decimalDigits = mantissaParts.length > 1 && mantissaParts[1] ? mantissaParts[1].length : 0;
        return exponent + decimalDigits;
      }
    }
    return 0;
  }

  // ===== TEMPLATE SYNC DETECTION METHODS =====

  /**
   * Get detailed template changes for a specific instrument
   * @param instrument - The instrument to analyze
   * @returns Detailed field-by-field comparison with current template
   */
  async getInstrumentTemplateChanges(instrument: Instrument): Promise<{
    instrumentId: string;
    instrumentName: string;
    hasTemplate: boolean;
    templateExists: boolean;
    fieldChanges: Array<{
      fieldName: string;
      fieldLabel: string;
      currentValue: string | number;
      templateValue: string | number;
      isCustomized: boolean;
    }>;
    hasChanges: boolean;
  }> {
    const result = {
      instrumentId: instrument.id,
      instrumentName: instrument.displayName,
      hasTemplate: !!instrument.instrumentReferenceId,
      templateExists: false,
      fieldChanges: [] as Array<{
        fieldName: string;
        fieldLabel: string;
        currentValue: string | number;
        templateValue: string | number;
        isCustomized: boolean;
      }>,
      hasChanges: false
    };

    // If no template reference, return empty result
    if (!instrument.instrumentReferenceId) {
      return result;
    }

    // Get the current template from catalog
    const currentTemplate = getInstrumentById(instrument.instrumentReferenceId);
    if (!currentTemplate) {
      return result; // Template no longer exists
    }

    result.templateExists = true;

    // Define trackable fields with their display labels
    const trackableFields = [
      { key: 'symbol', label: 'Symbol' },
      { key: 'displayName', label: 'Display Name' },
      { key: 'assetClass', label: 'Asset Class' },
      { key: 'subAssetClass', label: 'Sub Asset Class' },
      { key: 'marketSegment', label: 'Market Segment' },
      { key: 'contractSize', label: 'Contract Size' },
      { key: 'contractAsset', label: 'Contract Asset' },
      { key: 'incrementSize', label: 'Increment Size' },
      { key: 'incrementType', label: 'Increment Type' },
      { key: 'quoteCurrency', label: 'Quote Currency' }
    ];

    // Compare each field
    for (const field of trackableFields) {
      let currentValue: string | number;
      let templateValue: string | number;

      // Extract values with proper type casting
      switch (field.key) {
        case 'symbol':
          currentValue = instrument.symbol;
          templateValue = currentTemplate.symbol;
          break;
        case 'displayName':
          currentValue = instrument.displayName;
          templateValue = currentTemplate.name;
          break;
        case 'assetClass':
          currentValue = instrument.classification.assetClass;
          templateValue = currentTemplate.classification.assetClass;
          break;
        case 'subAssetClass':
          currentValue = instrument.classification.subAssetClass || '';
          templateValue = currentTemplate.classification.subAssetClass || '';
          break;
        case 'marketSegment':
          currentValue = instrument.classification.marketSegment || '';
          templateValue = currentTemplate.classification.marketSegment || '';
          break;
        case 'contractSize':
          currentValue = instrument.contractSize;
          templateValue = currentTemplate.contractSize;
          break;
        case 'contractAsset':
          currentValue = instrument.contractAsset;
          templateValue = currentTemplate.contractAsset;
          break;
        case 'incrementSize':
          currentValue = instrument.incrementSize;
          templateValue = currentTemplate.incrementSize;
          break;
        case 'incrementType':
          currentValue = instrument.incrementType;
          templateValue = currentTemplate.incrementType;
          break;
        case 'quoteCurrency':
          currentValue = instrument.quoteCurrency;
          templateValue = currentTemplate.quoteCurrency;
          break;
        default:
          continue; // Skip unknown fields
      }

      const isCustomized = instrument.customizedFields?.includes(field.key) || false;

      // Only include fields that have differences from template AND are not customized
      if (currentValue !== templateValue && !isCustomized) {
        result.fieldChanges.push({
          fieldName: field.key,
          fieldLabel: field.label,
          currentValue,
          templateValue,
          isCustomized: false  // Will always be false since we filtered out customized fields
        });
      }
    }

    result.hasChanges = result.fieldChanges.length > 0;
    return result;
  }

  /**
   * Apply template updates based on user accept/reject decisions
   * @param instrumentId - ID of instrument to update
   * @param fieldDecisions - Map of field names to accept (true) or reject (false)
   * @returns Promise resolving to updated instrument
   */
  async applyTemplateUpdates(
    instrumentId: string, 
    fieldDecisions: Record<string, boolean>
  ): Promise<Instrument> {
    // Get current instrument
    const instrument = await this.get(instrumentId);
    if (!instrument) {
      throw new Error(`Instrument not found: ${instrumentId}`);
    }

    // Get template for reference
    if (!instrument.instrumentReferenceId) {
      throw new Error('Cannot apply template updates - instrument has no template reference');
    }

    const currentTemplate = getInstrumentById(instrument.instrumentReferenceId);
    if (!currentTemplate) {
      throw new Error('Cannot apply template updates - template no longer exists');
    }

    // Prepare updates
    const updates: Partial<Instrument> = {};
    const newCustomizedFields = [...(instrument.customizedFields || [])];

    // Process each field decision
    for (const [fieldName, shouldAccept] of Object.entries(fieldDecisions)) {
      if (shouldAccept) {
        // Accept: Update field value and remove from customized fields
        switch (fieldName) {
          case 'symbol':
            updates.symbol = currentTemplate.symbol;
            break;
          case 'displayName':
            updates.displayName = currentTemplate.name;
            break;
          case 'assetClass':
            if (!updates.classification) {
              updates.classification = { ...instrument.classification };
            }
            updates.classification.assetClass = currentTemplate.classification.assetClass;
            break;
          case 'subAssetClass':
            if (!updates.classification) {
              updates.classification = { ...instrument.classification };
            }
            if (currentTemplate.classification.subAssetClass !== undefined) {
              updates.classification.subAssetClass = currentTemplate.classification.subAssetClass;
            } else {
              delete updates.classification.subAssetClass;
            }
            break;
          case 'marketSegment':
            if (!updates.classification) {
              updates.classification = { ...instrument.classification };
            }
            updates.classification.marketSegment = currentTemplate.classification.marketSegment;
            break;
          case 'contractSize':
            updates.contractSize = currentTemplate.contractSize;
            break;
          case 'contractAsset':
            updates.contractAsset = currentTemplate.contractAsset;
            break;
          case 'incrementSize':
            updates.incrementSize = currentTemplate.incrementSize;
            // Also update precision when increment size changes
            updates.pricePrecision = this.calculatePrecisionFromIncrementSize(currentTemplate.incrementSize);
            break;
          case 'incrementType':
            updates.incrementType = currentTemplate.incrementType;
            break;
          case 'quoteCurrency':
            updates.quoteCurrency = currentTemplate.quoteCurrency;
            break;
        }
        
        // Remove from customized fields (user is accepting template value)
        const index = newCustomizedFields.indexOf(fieldName);
        if (index > -1) {
          newCustomizedFields.splice(index, 1);
        }
      } else {
        // Reject: Keep current value and add to customized fields
        if (!newCustomizedFields.includes(fieldName)) {
          newCustomizedFields.push(fieldName);
        }
      }
    }

    // Update customized fields list
    updates.customizedFields = newCustomizedFields;

    // Update template status - check if any non-customized fields still differ
    const templateStatus = this.getTemplateStatus({
      ...instrument,
      ...updates,
      customizedFields: newCustomizedFields
    } as Instrument);
    updates.templateStatus = templateStatus;

    // Apply updates
    return await this.update(instrumentId, updates);
  }

  // ===== TEMPLATE REVIEW SESSION PERSISTENCE =====

  /**
   * Save template review session state to localStorage
   * @param sessionData - Session data to persist
   */
  saveTemplateReviewSession(sessionData: {
    instrumentDecisions: Record<string, Record<string, boolean>>; // instrumentId -> fieldName -> accept/reject
    processedInstruments: string[]; // List of instrument IDs that have been committed
    lastActiveTimestamp: number;
  }): void {
    try {
      localStorage.setItem('templateReviewSession', JSON.stringify(sessionData));
    } catch (error) {
      console.warn('Failed to save template review session:', error);
    }
  }

  /**
   * Load template review session state from localStorage
   * @returns Saved session data or null if none exists
   */
  loadTemplateReviewSession(): {
    instrumentDecisions: Record<string, Record<string, boolean>>;
    processedInstruments: string[];
    lastActiveTimestamp: number;
  } | null {
    try {
      const saved = localStorage.getItem('templateReviewSession');
      if (!saved) return null;
      
      const sessionData = JSON.parse(saved);
      
      // Check if session is too old (24 hours)
      const now = Date.now();
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours
      if (now - sessionData.lastActiveTimestamp > maxAge) {
        this.clearTemplateReviewSession();
        return null;
      }
      
      return sessionData;
    } catch (error) {
      console.warn('Failed to load template review session:', error);
      return null;
    }
  }

  /**
   * Clear template review session from localStorage
   */
  clearTemplateReviewSession(): void {
    try {
      localStorage.removeItem('templateReviewSession');
    } catch (error) {
      console.warn('Failed to clear template review session:', error);
    }
  }

  /**
   * Check if instrument catalog has updates compared to user's last known version
   * @param userLastKnownVersion - Version from user's BasicSettings.instrumentCatalog.lastKnownVersion
   * @returns Promise resolving to instrument catalog update information
   */
  async checkInstrumentCatalogUpdates(userLastKnownVersion?: string): Promise<{
    hasUpdates: boolean;
    currentVersion: string;
    userVersion: string | null;
    versionsBehind: number;
    newFeatures: string[];
    recommendedAction: 'none' | 'review' | 'sync';
  }> {
    try {
      // Import catalog metadata dynamically to avoid circular dependencies
      const { CATALOG_METADATA, isVersionNewer, getVersionKey } = await import('@/data/instrumentCatalog');
      
      const currentVersion = CATALOG_METADATA.version;
      const userVersion = userLastKnownVersion || null;
      
      // If user has no version tracked, they need to sync
      if (!userVersion) {
        return {
          hasUpdates: true,
          currentVersion,
          userVersion,
          versionsBehind: 1,
          newFeatures: CATALOG_METADATA.releaseNotes,
          recommendedAction: 'sync'
        };
      }

      // Check if catalog is newer
      const hasUpdates = isVersionNewer(currentVersion, userVersion);
      
      if (!hasUpdates) {
        return {
          hasUpdates: false,
          currentVersion,
          userVersion,
          versionsBehind: 0,
          newFeatures: [],
          recommendedAction: 'none'
        };
      }

      // Calculate how many versions behind
      const currentKey = getVersionKey(currentVersion);
      const userKey = getVersionKey(userVersion);
      const versionsBehind = Math.max(1, Math.floor((currentKey - userKey) / 100));
      
      // Determine recommended action based on breaking changes
      const recommendedAction = CATALOG_METADATA.breakingChanges ? 'review' : 'sync';
      
      return {
        hasUpdates: true,
        currentVersion,
        userVersion,
        versionsBehind,
        newFeatures: CATALOG_METADATA.releaseNotes,
        recommendedAction
      };
      
    } catch (error) {
      console.error('Failed to check instrument catalog updates:', error);
      throw new Error('Failed to check instrument catalog updates');
    }
  }


  /**
   * Update user's last known instrument catalog version
   * Typically called after successful import or manual sync
   * @param version - New instrument catalog version to record
   * @returns Promise resolving to success
   */
  async updateUserInstrumentCatalogVersion(version: string): Promise<boolean> {
    try {
      // TODO: This will need to integrate with BasicSettings service when it exists
      // For now, this is a placeholder that logs the intent
      console.log(`Would update user's instrument catalog version to: ${version}`);
      
      // TODO: [PHASE 3] Integration with BasicSettings service:
      // const settingsService = new BasicSettingsService();
      // const settings = await settingsService.getCurrentSettings();
      // await settingsService.update(settings.id, {
      //   instrumentCatalog: {
      //     ...settings.instrumentCatalog,
      //     lastKnownVersion: version,
      //     lastVersionCheck: new Date().toISOString()
      //   }
      // });
      
      return true;
    } catch (error) {
      console.error('Failed to update user instrument catalog version:', error);
      return false;
    }
  }

  /**
   * Check and update template status for a single instrument
   * @param instrument - The instrument to check and update
   * @returns Promise resolving to updated instrument with template status
   */
  async checkAndUpdateTemplateStatus(instrument: Instrument): Promise<Instrument> {
    try {
      let newStatus: 'up-to-date' | 'outdated' | 'custom';

      // If no reference ID, it's a custom instrument
      if (!instrument.instrumentReferenceId) {
        newStatus = 'custom';
      } else {
        // Get the current template from catalog
        const currentTemplate = getInstrumentById(instrument.instrumentReferenceId);
        if (!currentTemplate) {
          newStatus = 'custom'; // Referenced template no longer exists
        } else {
          // Only check fields that user has NOT customized
          const nonCustomizedFields = [
            'contractSize', 'contractAsset', 'incrementSize', 'incrementType', 'quoteCurrency'
          ].filter(field => !instrument.customizedFields?.includes(field));

          // Check if any non-customized fields differ from template
          const hasOutdatedFields = nonCustomizedFields.some(field => {
            switch (field) {
              case 'contractSize':
                return currentTemplate.contractSize !== instrument.contractSize;
              case 'contractAsset':
                return currentTemplate.contractAsset !== instrument.contractAsset;
              case 'incrementSize':
                return currentTemplate.incrementSize !== instrument.incrementSize;
              case 'incrementType':
                return currentTemplate.incrementType !== instrument.incrementType;
              case 'quoteCurrency':
                return currentTemplate.quoteCurrency !== instrument.quoteCurrency;
              default:
                return false;
            }
          });

          newStatus = hasOutdatedFields ? 'outdated' : 'up-to-date';
        }
      }

      // Update the instrument if status changed
      if (instrument.templateStatus !== newStatus) {
        const updatedInstrument = await this.update(instrument.id, { templateStatus: newStatus });
        return updatedInstrument;
      }

      return instrument;
    } catch (error) {
      console.error('Failed to check template status:', error);
      // Update to custom on error
      if (instrument.templateStatus !== 'custom') {
        return await this.update(instrument.id, { templateStatus: 'custom' });
      }
      return instrument;
    }
  }

  /**
   * Get template status for display (without updating database)
   * @param instrument - The instrument to check
   * @returns Current template status
   * 
   * NOTE: This is the authoritative method for template status detection.
   * Used by UI badges, filtering, and Template Review Modal system.
   */
  getTemplateStatus(instrument: Instrument): 'up-to-date' | 'outdated' | 'custom' {
    // Return stored status if available, otherwise calculate
    if (instrument.templateStatus) {
      return instrument.templateStatus;
    }

    // Fallback calculation for instruments without stored status
    if (!instrument.instrumentReferenceId) {
      return 'custom';
    }

    const currentTemplate = getInstrumentById(instrument.instrumentReferenceId);
    if (!currentTemplate) {
      return 'custom';
    }

    const nonCustomizedFields = [
      'symbol', 'displayName', 'assetClass', 'subAssetClass', 'marketSegment',
      'contractSize', 'contractAsset', 'incrementSize', 'incrementType', 'quoteCurrency'
    ].filter(field => !instrument.customizedFields?.includes(field));

    const hasOutdatedFields = nonCustomizedFields.some(field => {
      switch (field) {
        case 'symbol':
          return currentTemplate.symbol !== instrument.symbol;
        case 'displayName':
          return currentTemplate.name !== instrument.displayName;
        case 'assetClass':
          return currentTemplate.classification.assetClass !== instrument.classification.assetClass;
        case 'subAssetClass':
          return (currentTemplate.classification.subAssetClass || '') !== (instrument.classification.subAssetClass || '');
        case 'marketSegment':
          return (currentTemplate.classification.marketSegment || '') !== (instrument.classification.marketSegment || '');
        case 'contractSize':
          return currentTemplate.contractSize !== instrument.contractSize;
        case 'contractAsset':
          return currentTemplate.contractAsset !== instrument.contractAsset;
        case 'incrementSize':
          return currentTemplate.incrementSize !== instrument.incrementSize;
        case 'incrementType':
          return currentTemplate.incrementType !== instrument.incrementType;
        case 'quoteCurrency':
          return currentTemplate.quoteCurrency !== instrument.quoteCurrency;
        default:
          return false;
      }
    });

    return hasOutdatedFields ? 'outdated' : 'up-to-date';
  }

  // ===== SMART DELETE OPERATIONS =====

  /**
   * Archive instruments by setting status to 'archived'
   *
   * Uses BaseService.update() for each instrument to ensure:
   * - Sync operations are logged via SyncOperationService
   * - Cross-tab notifications are sent via syncCoordinatorService
   * - Records remain queryable (not tombstoned)
   *
   * Note: This sets EntityStatus, NOT deletedAt tombstone. Archived instruments
   * can still be filtered and viewed - they're just hidden from default views.
   *
   * @param instrumentIds - Array of instrument IDs to archive
   * @returns Promise resolving to detailed results of the operation
   */
  async archiveInstruments(instrumentIds: string[]): Promise<BulkDeleteResult> {
    const successIds: string[] = [];
    const failedIds: string[] = [];
    const errors: Record<string, string> = {};

    for (const id of instrumentIds) {
      try {
        await this.update(id, { status: 'archived' as EntityStatus });
        successIds.push(id);
      } catch (error) {
        failedIds.push(id);
        errors[id] = error instanceof Error ? error.message : 'Unknown error';
      }
    }

    console.log(`Archived ${successIds.length} instruments, ${failedIds.length} failed`);

    return {
      successIds,
      failedIds,
      ...(Object.keys(errors).length > 0 && { errors })
    };
  }

  /**
   * Restore archived instruments by setting status back to 'active'
   *
   * Uses BaseService.update() for each instrument to ensure:
   * - Sync operations are logged via SyncOperationService
   * - Cross-tab notifications are sent via syncCoordinatorService
   * - Records become visible in default (active) views again
   *
   * Note: This sets EntityStatus back to 'active'. Only affects archived instruments.
   *
   * @param instrumentIds - Array of instrument IDs to restore
   * @returns Promise resolving to detailed results of the operation
   */
  async restoreInstruments(instrumentIds: string[]): Promise<BulkDeleteResult> {
    const successIds: string[] = [];
    const failedIds: string[] = [];
    const errors: Record<string, string> = {};

    for (const id of instrumentIds) {
      try {
        await this.update(id, { status: 'active' as EntityStatus });
        successIds.push(id);
      } catch (error) {
        failedIds.push(id);
        errors[id] = error instanceof Error ? error.message : 'Unknown error';
      }
    }

    console.log(`Restored ${successIds.length} instruments, ${failedIds.length} failed`);

    return {
      successIds,
      failedIds,
      ...(Object.keys(errors).length > 0 && { errors })
    };
  }

  /**
   * Soft delete instruments (tombstone for sync)
   *
   * Uses BaseService.softDelete() for each instrument to ensure:
   * - Tombstone fields are set (deletedAt, deletedByDevice)
   * - Sync operations are logged via SyncOperationService
   * - Cross-tab notifications are sent via syncCoordinatorService
   * - Records are excluded from default queries but remain for sync
   *
   * This is the correct method for user-initiated "Delete" actions.
   *
   * @param instrumentIds - Array of instrument IDs to soft delete
   * @returns Promise resolving to detailed results of the operation
   */
  async softDeleteInstruments(instrumentIds: string[]): Promise<BulkDeleteResult> {
    const successIds: string[] = [];
    const failedIds: string[] = [];
    const errors: Record<string, string> = {};

    for (const id of instrumentIds) {
      try {
        await this.softDelete(id);
        successIds.push(id);
      } catch (error) {
        failedIds.push(id);
        errors[id] = error instanceof Error ? error.message : 'Unknown error';
      }
    }

    console.log(`Soft deleted ${successIds.length} instruments, ${failedIds.length} failed`);

    return {
      successIds,
      failedIds,
      ...(Object.keys(errors).length > 0 && { errors })
    };
  }

  /**
   * @internal
   * Hard delete instruments (permanent removal from database)
   *
   * ⚠️ INTERNAL USE ONLY - For post-sync cleanup operations.
   * DO NOT USE for user-initiated delete actions.
   *
   * Uses BaseService.hardDelete() which does NOT log sync operations.
   * This is intentional for cleanup of already-synced tombstoned records.
   *
   * For user-initiated deletes, use softDeleteInstruments() instead.
   *
   * @param instrumentIds - Array of instrument IDs to permanently delete
   * @returns Promise resolving to detailed results of the operation
   */
  async hardDeleteInstruments(instrumentIds: string[]): Promise<BulkDeleteResult> {
    const successIds: string[] = [];
    const failedIds: string[] = [];
    const errors: Record<string, string> = {};

    for (const id of instrumentIds) {
      try {
        await this.hardDelete(id);
        successIds.push(id);
      } catch (error) {
        failedIds.push(id);
        errors[id] = error instanceof Error ? error.message : 'Unknown error';
      }
    }

    // Notify cross-tab for UI refresh (instruments grid needs to update)
    // BaseService.hardDelete() doesn't notify by design, but bulk UI operations need it
    const firstSuccessId = successIds[0];
    if (firstSuccessId) {
      syncCoordinatorService.notifyChange(this.tableName, firstSuccessId);
    }

    console.log(`Hard deleted ${successIds.length} instruments, ${failedIds.length} failed`);

    return {
      successIds,
      failedIds,
      ...(Object.keys(errors).length > 0 && { errors })
    };
  }

  /**
   * Execute smart delete operation based on usage analysis results
   *
   * Separates instruments by recommended action and executes appropriate
   * method for each group:
   * - 'archive': Sets status to 'archived' (record remains, just hidden)
   * - 'hard-delete': Soft deletes (tombstones for sync, excluded from queries)
   *
   * Both operations log sync operations for cross-device consistency.
   *
   * @param instruments - Array of InstrumentUsageResult with recommended actions
   * @returns Promise resolving to structured operation results
   */
  async executeSmartDelete(instruments: Array<{ instrumentId: string; recommendedAction: 'archive' | 'hard-delete' }>): Promise<{
    archive: BulkDeleteResult;
    softDelete: BulkDeleteResult;
    total: number;
  }> {
    // Separate instruments by action
    const toArchive = instruments
      .filter(inst => inst.recommendedAction === 'archive')
      .map(inst => inst.instrumentId);

    const toDelete = instruments
      .filter(inst => inst.recommendedAction === 'hard-delete')
      .map(inst => inst.instrumentId);

    // Execute operations in parallel for efficiency
    const [archiveResult, softDeleteResult] = await Promise.all([
      toArchive.length > 0
        ? this.archiveInstruments(toArchive)
        : { successIds: [], failedIds: [] } as BulkDeleteResult,
      toDelete.length > 0
        ? this.softDeleteInstruments(toDelete)
        : { successIds: [], failedIds: [] } as BulkDeleteResult
    ]);

    const result = {
      archive: archiveResult,
      softDelete: softDeleteResult,
      total: archiveResult.successIds.length + softDeleteResult.successIds.length
    };

    console.log('Smart delete operation completed:', {
      archived: archiveResult.successIds.length,
      deleted: softDeleteResult.successIds.length,
      total: result.total
    });

    return result;
  }
}

// Export singleton instance
export const instrumentService = new InstrumentService();